import { Box, Tooltip } from '@mui/material';
import type { ProjectSensorStatus } from '@si/types';
import { formatNumber, useTranslate } from '@si/utils';
import { Fragment } from 'react';

import { Stack } from '../Stack/Stack';
import { Text } from '../Text/Text';
import { useColors } from '../ThemeProvider';

export function SensorStatusBar({ status }: { status: ProjectSensorStatus | null | undefined }) {
  const { t, translate } = useTranslate();
  const { success, warning, error, grey, surfaceHover, mode } = useColors();
  const groups = [
    {
      label: translate(t.normal),
      count: status?.sensorCountOk,
      color: mode === 'light' ? '#159947' : success,
    },
    {
      label: translate(t.warning),
      count: status?.sensorCountWarning,
      color: mode === 'light' ? '#FFC107' : warning,
    },
    {
      label: translate(t.alert),
      count: status?.sensorCountError,
      color: mode === 'light' ? '#DC2626' : error,
    },
    { label: translate(t.offline), count: status?.sensorCountInactive, color: grey },
  ];
  const total = status ? groups.reduce((sum, group) => sum + (group.count ?? 0), 0) : undefined;
  const statusDescription = groups
    .map(({ label, count }) => `${label} ${count == null ? '—' : formatNumber(count)}`)
    .join(', ');

  return (
    <Stack gap="0.5rem" role="group" aria-label={translate(t.sensorStatus)} minWidth={0}>
      <Stack direction="row" justifyContent="space-between" gap="1rem">
        <Text color="darkGrey" fontSize="0.875rem">
          {translate(t.sensorStatus)}
        </Text>
        <Text color="darkGrey" fontSize="0.875rem">
          {total == null ? '—' : formatNumber(total)} {translate(t.sensors).toLocaleLowerCase()}
        </Text>
      </Stack>
      <Box
        role="img"
        aria-label={statusDescription}
        sx={{
          display: 'flex',
          height: '0.625rem',
          borderRadius: '1rem',
          overflow: 'hidden',
          backgroundColor: surfaceHover,
        }}
      >
        {groups
          .filter(({ count }) => count != null && count > 0)
          .map(({ label, count, color }) => (
            <Tooltip key={label} title={`${label} ${formatNumber(count ?? 0)}`}>
              <Box sx={{ flex: count, minWidth: '2px', backgroundColor: color }} />
            </Tooltip>
          ))}
      </Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        gap="0.5rem"
        flexWrap="wrap"
      >
        {groups.map(({ label, count }, index) => (
          <Fragment key={label}>
            {index > 0 && (
              <Box component="span" aria-hidden="true" sx={{ color: 'text.secondary' }}>
                ·
              </Box>
            )}
            <Text color="darkGrey" fontSize="0.875rem">
              {label} {count == null ? '—' : formatNumber(count)}
            </Text>
          </Fragment>
        ))}
      </Stack>
    </Stack>
  );
}
